import { useState, useEffect, useRef, useCallback } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';
import { focusAfterPaint } from '@/utils/dom';

export interface CustomLocationData {
  id?: number;
  location?: string;
  latitude?: number;
  longitude?: number;
}

export interface AddEditCustomLocationSaveData {
  id?: number;
  location: string;
  latitude: number;
  longitude: number;
}

export interface AddEditCustomLocationModalProps {
  open: boolean;
  mode?: 'add' | 'edit';
  customLocation?: CustomLocationData;
  theme?: string;
  saving?: boolean;
  onSave: (data: AddEditCustomLocationSaveData) => void;
  onCancel: () => void;
}

export function AddEditCustomLocationModal({
  open,
  mode = 'add',
  customLocation = {},
  theme,
  saving = false,
  onSave,
  onCancel,
}: AddEditCustomLocationModalProps) {
  const [location, setLocation] = useState('');
  const [coordinates, setCoordinates] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const modalTitle = mode === 'edit' ? 'Edit Custom Location' : 'Add Custom Location';
  const saveButtonText = mode === 'edit' ? 'Update' : 'Save';

  // Populate/reset form when modal opens
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && customLocation) {
        setLocation(customLocation.location || '');
        setCoordinates('' + customLocation.latitude + ',' + customLocation.longitude);
      } else {
        setLocation('');
        setCoordinates('');
      }

      focusAfterPaint(inputRef);
    }
  }, [open, mode, customLocation]);

  const handleSave = useCallback(() => {
    if (!location || !location.trim()) {
      (window as any).App?.showPopupNotification?.('Location required.', 'error');
      return;
    }

    if (!coordinates || !coordinates.trim()) {
      (window as any).App?.showPopupNotification?.('Coordinates required.', 'error');
      return;
    }

    const coordinatesArr = coordinates.split(',');
    if (!coordinatesArr || coordinatesArr.length !== 2) {
      (window as any).App?.showPopupNotification?.('Coordinates in incorrect format.', 'error');
      return;
    }

    const latitude = parseFloat(coordinatesArr[0]);
    const longitude = parseFloat(coordinatesArr[1]);

    if (isNaN(latitude) || latitude < -90.0 || latitude > 90.0 || isNaN(longitude) || longitude < -180.0 || longitude > 180.0) {
      (window as any).App?.showPopupNotification?.('Invalid coordinates.', 'error');
      return;
    }

    onSave({
      id: mode === 'edit' ? customLocation?.id : undefined,
      location: location.trim(),
      latitude,
      longitude,
    });
  }, [location, coordinates, mode, customLocation, onSave]);

  return (
    <BaseFormModal
      open={open}
      title={modalTitle}
      theme={theme}
      dialogClass="kstc-dialog--form"
      saveText={saving ? 'Saving...' : saveButtonText}
      saveDisabled={saving}
      busy={saving}
      onSave={handleSave}
      onCancel={onCancel}
    >
      <div className="kstc-form-group">
        <label className="kstc-label">Location</label>
        <input
          ref={inputRef}
          type="text"
          className="kstc-input kst-location-input"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Enter location name..."
        />
      </div>

      <div className="kstc-form-group">
        <label className="kstc-label">Coordinates</label>
        <input
          type="text"
          className="kstc-input"
          value={coordinates}
          onChange={(e) => setCoordinates(e.target.value)}
          placeholder="e.g. 40.68925,-74.04450"
        />
        <span className="kstc-hint">Format: latitude,longitude (e.g. 40.68925,-74.04450)</span>
      </div>
    </BaseFormModal>
  );
}

(window as any).AddEditCustomLocationModalReact = AddEditCustomLocationModal;
